import React from 'react';
import { useNavigate } from 'react-router-dom';
import { GroupMember } from '../types';
import { getInitials, getRoleBadge, formatDate } from './helpers';
import styles from '../../../../../../styles/PointScheme.module.css';
import buttonStyles from '../../../../../../styles/Buttons.module.css';

interface UserCardProps {
  member: GroupMember;
  isSelected: boolean;
  onSelect: (memberId: string) => void;
  onEdit: (member: GroupMember) => void;
  onDelete: (member: GroupMember) => void;
  groupId: string;
  canManageUsers: boolean;
}

const UserCard: React.FC<UserCardProps> = ({
  member,
  isSelected,
  onSelect,
  onEdit,
  onDelete,
  groupId,
  canManageUsers
}) => {
  const navigate = useNavigate();
  const roleBadge = getRoleBadge(member);
  const fullName = member.user.full_name || `${member.user.first_name} ${member.user.last_name}`;
  
  const statusText = member.status 
    ? (member.status.charAt(0).toUpperCase() + member.status.slice(1).toLowerCase())
    : (member.is_active ? 'Active' : 'Inactive');
  const isActiveStatus = statusText.toLowerCase() === 'active' || member.is_active;

  return (
    <div className={styles.categoryCard}>
      {/* Card Header */}
      <div className={styles.cardHeader}>
        <div className={styles.cardHeaderLeft}>
          {/* Checkbox */}
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onSelect(member.id)}
            onClick={(e) => e.stopPropagation()}
            style={{ 
              width: '14px', 
              height: '14px', 
              accentColor: 'var(--primary)',
              marginRight: '0.5rem',
              flexShrink: 0,
              cursor: 'pointer'
            }}
          />
          
          {/* Avatar Icon Wrapper */}
          <div 
            className={styles.categoryIconWrapper} 
            style={{ backgroundColor: 'var(--primary-light)' }}
          >
            {member.user.avatar ? (
              <img 
                src={member.user.avatar} 
                alt={fullName}
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'cover', 
                  borderRadius: '12px' 
                }}
              />
            ) : (
              <span style={{ 
                fontSize: '1.25rem', 
                fontWeight: '600', 
                color: 'var(--primary)' 
              }}>
                {getInitials(member.user.first_name, member.user.last_name)}
              </span>
            )}
          </div>
          
          {/* Title Section */}
          <div className={styles.cardTitleSection}>
            <h3 className={styles.cardTitle}>{fullName}</h3>
            <div className={styles.cardDescription}>
              {member.user.email || 'No email'}
            </div>
          </div>
        </div>
        
        <div className={styles.cardHeaderRight}>
          {/* Status Badge */}
          {(() => {
            const statusText = member.status 
              ? (member.status.charAt(0).toUpperCase() + member.status.slice(1).toLowerCase())
              : (member.is_active ? 'Active' : 'Inactive');
            const isActiveStatus = statusText.toLowerCase() === 'active' || member.is_active;
            
            return (
              <div 
                style={{
                  backgroundColor: isActiveStatus ? '#d1fae5' : 'var(--gray-lighter)',
                  color: isActiveStatus ? '#0d946e' : 'var(--gray)',
                  borderColor: isActiveStatus ? '#10b981' : 'var(--border)',
                  padding: '0.375rem 0.75rem',
                  borderRadius: '20px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  marginRight: '0.5rem',
                  border: '1px solid',
                  display: 'inline-flex',
                  alignItems: 'center'
                }}
              >
                {statusText}
              </div>
            );
          })()}

          {/* Action Buttons */}
          <div className={styles.cardActions}>
            <button 
              className={`${buttonStyles.btn} ${buttonStyles.btnSecondary} ${buttonStyles.btnIcon}`}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/group-management/groups/${groupId}/users/${member.user.id}/performance`);
              }}
              title="View Performance Dashboard"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 3v18h18"></path>
                <path d="M18 17V9"></path>
                <path d="M13 17V5"></path>
                <path d="M8 17v-3"></path>
              </svg>
            </button>
            {canManageUsers && (
              <>
                <button 
                  className={`${buttonStyles.btn} ${buttonStyles.btnSecondary} ${buttonStyles.btnIcon}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(member);
                  }}
                  title="Edit Member"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                  </svg>
                </button>
                
                <button 
                  className={`${buttonStyles.btn} ${buttonStyles.btnDanger} ${buttonStyles.btnIcon}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(member);
                  }}
                  title="Remove From Group"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3,6 5,6 21,6"></polyline>
                    <path d="M19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"></path>
                    <line x1="10" y1="11" x2="10" y2="17"></line>
                    <line x1="14" y1="11" x2="14" y2="17"></line>
                  </svg>
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Card Body - Tags and Metadata in Single Row */}
      <div className={styles.cardBody}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.75rem',marginLeft: '1.8rem'}}>
          {/* Left side - Tags */}
          <div className={styles.cardTags} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem'}}>
            {/* Role Badge */}
            <div 
              className={styles.categoryTag}
              style={{
                backgroundColor: roleBadge.style.backgroundColor || (roleBadge.style.background ? 'transparent' : 'var(--primary-light)'),
                color: roleBadge.style.color || 'var(--primary-dark)',
                borderColor: (roleBadge.style as any).borderColor || roleBadge.style.backgroundColor || 'var(--primary)',
                padding: '0.375rem 0.625rem',
                borderRadius: '30px',
                fontSize: '0.625rem',
                fontWeight: '600',
                border: '1px solid',
                ...(roleBadge.style.background ? { background: roleBadge.style.background } : {}),
                ...((roleBadge.style as any).boxShadow ? { boxShadow: (roleBadge.style as any).boxShadow } : {})
              }}
              title={roleBadge.title}
            >
              <span>{roleBadge.text}</span>
            </div>

            {/* Custom Permissions Badge */}
            {member.has_custom_permissions && (
              <div 
                className={styles.categoryTag}
                style={{
                  backgroundColor: 'var(--warning-light)',
                  color: 'var(--warning-dark)',
                  borderColor: 'var(--warning)',
                  padding: '0.375rem 0.625rem',
                  borderRadius: '30px',
                  fontSize: '0.625rem',
                  fontWeight: '600',
                  border: '1px solid',
                  display: 'inline-flex',
                  alignItems: 'center'
                }}
                title="Has custom permissions"
              >
                <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor" style={{ marginRight: '0.375rem' }}>
                  <path d="M8 4.143A1.071 1.071 0 1 0 8 2a1.071 1.071 0 0 0 0 2.143m-4.668 1.47 3.24.316v2.5l-.323 4.585A.383.383 0 0 0 7 13.14l.826-4.017c.045-.18.301-.18.346 0L9 13.139a.383.383 0 0 0 .752-.125L9.43 8.43v-2.5l3.239-.316a.38.38 0 0 0-.047-.756H3.379a.38.38 0 0 0-.047.756Z"/>
                  <path d="M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0M1 8a7 7 0 1 1 14 0A7 7 0 0 1 1 8"/>
                </svg>
                <span>Custom Permissions</span>
              </div>
            )}

            {/* Points Earned */}
            {member.points_earned !== undefined && member.points_earned > 0 && (
              <div className={styles.pointsTag}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '0.375rem' }}>
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                </svg>
                <span style={{ fontSize: '0.625rem' }}>{member.points_earned} Points</span>
              </div>
            )}
          </div>

          {/* Right side - Metadata Items */}
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.75rem' }}>
            <div className={styles.metadataItem} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--gray)', flexShrink: 0 }}>
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
              <span className={styles.metadataText} style={{ fontSize: '0.75rem', color: 'var(--gray)' }}>Joined: {formatDate(member.joined_at)}</span>
            </div>

            {member.last_active && (
              <div className={styles.metadataItem} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--gray)', flexShrink: 0 }}>
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
                <span className={styles.metadataText} style={{ fontSize: '0.75rem', color: 'var(--gray)' }}>Last Active: {formatDate(member.last_active)}</span>
              </div>
            )}

            {member.is_verified !== undefined && (
              <div className={styles.metadataItem} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--gray)', flexShrink: 0 }}>
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
                <span className={styles.metadataText} style={{ fontSize: '0.75rem', color: 'var(--gray)' }}>
                  {member.is_verified ? 'Verified' : 'Not Verified'}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserCard;
